import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { StageLadder } from 'services/assist-api/entities/ladder-data/ladder-datas';
import './ladder.scss';

// ESLint thinks these are unused props when used with D3:
export type StageLadderNode = {
  ladderData: StageLadder[];
  width: number;
  margin?: {
    left?: number;
    right?: number;
  };
  scales: { y: d3.ScaleLinear<number, number> };
  // eslint-disable-next-line react/no-unused-prop-types
  ladderName?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  inactive?: boolean;
};

export const LadderNode = (props: StageLadderNode): Element | null => {
  const {
    ladderData,
    width,
    margin: { left: marginLeft = 0, right: marginRight = 0 } = {},
    scales,
    ladderName = 'Stanine',
    inactive
  } = props;

  const upperBound = {
    top: scales.y(ladderData[ladderData.length - 1].upperBound),
    bottom: scales.y(ladderData[ladderData.length - 1].lowerBound)
  };
  const lowerBound = {
    top: scales.y(ladderData[0].upperBound),
    bottom: scales.y(ladderData[0].lowerBound)
  };
  const stageLadder = d3.create('svg:g').classed('ladder', true).classed('inactive', inactive);

  // Ladder Name
  stageLadder
    .append('foreignObject')
    .attr('width', width + marginLeft + marginRight)
    .attr('height', 1)
    .attr('overflow', 'visible')
    .attr('x', -marginLeft)
    .attr('y', upperBound.top)
    .append('xhtml:div')
    .classed('ladder-name', true)
    .html(ladderName);

  const rungBlock = stageLadder
    .selectAll('g.rung')
    .data(ladderData)
    .join('g')
    .attr('class', 'rung');
  rungBlock
    .append('rect')
    .classed('ladder-bg', true)
    .attr('x', 0)
    .attr('y', (d) => scales.y(d.upperBound))
    .attr('width', width)
    .attr('height', (d) => scales.y(d.lowerBound) - scales.y(d.upperBound));

  rungBlock
    .append('foreignObject')
    .attr('x', 0)
    .attr('y', (d) => scales.y(d.upperBound))
    .attr('width', width)
    .attr('height', (d) => scales.y(d.lowerBound) - scales.y(d.upperBound))
    .attr('overflow', 'visible')
    .append('xhtml:div')
    .attr('class', 'd-flex h-100')
    .append('div')
    .attr('class', 'text-capitalize w-100 align-self-center text-center level-label')
    .html((d: StageLadder) => (d.stageName?.length > 2 ? d.stageName[0] : d.stageName));

  // Lader rungs / dividers
  stageLadder
    .selectAll('line.ladder')
    .data([...ladderData].slice(0, ladderData.length - 1))
    .join('line')
    .classed('ladder-divider', true)
    .attr('x1', 0)
    .attr('y1', (d: StageLadder) => scales.y(d.upperBound))
    .attr('x2', width)
    .attr('y2', (d: StageLadder) => scales.y(d.upperBound))
    .attr('stroke', 'black');

  // Ladder sides
  stageLadder
    .append('line')
    .attr('y1', lowerBound.bottom - (lowerBound.bottom - lowerBound.top) / 2)
    .attr('y2', upperBound.top + (upperBound.bottom - upperBound.top) / 2)
    .attr('x1', 0)
    .attr('x2', 0)
    .classed('ladder-side', true)
    .attr('stroke', 'black');

  stageLadder
    .append('line')
    .attr('y1', lowerBound.bottom - (lowerBound.bottom - lowerBound.top) / 2)
    .attr('y2', upperBound.top + (upperBound.bottom - upperBound.top) / 2)
    .attr('x1', width)
    .attr('x2', width)
    .attr('stroke', 'black')
    .classed('ladder-side', true);

  // Ladder sides - ends
  stageLadder
    .append('line')
    .attr('y1', lowerBound.bottom - (lowerBound.bottom - lowerBound.top) / 2)
    .attr('y2', lowerBound.bottom)
    .attr('x1', 0)
    .attr('x2', 0)
    .attr('stroke', 'black')
    .attr('stroke-dasharray', '4')
    .classed('ladder-side', true)
    .classed('ladder-end', true);

  stageLadder
    .append('line')
    .attr('y1', lowerBound.bottom - (lowerBound.bottom - lowerBound.top) / 2)
    .attr('y2', lowerBound.bottom)
    .attr('x1', width)
    .attr('x2', width)
    .attr('stroke', 'black')
    .attr('stroke-dasharray', '4')
    .classed('ladder-side', true)
    .classed('ladder-end', true);

  stageLadder
    .append('line')
    .attr('y1', upperBound.top)
    .attr('y2', upperBound.top + (upperBound.bottom - upperBound.top) / 2)
    .attr('x1', 0)
    .attr('x2', 0)
    .attr('stroke', 'black')
    .attr('stroke-dasharray', '4')
    .classed('ladder-side', true)
    .classed('ladder-end', true);

  stageLadder
    .append('line')
    .attr('y1', upperBound.top)
    .attr('y2', upperBound.top + (upperBound.bottom - upperBound.top) / 2)
    .attr('x1', width)
    .attr('x2', width)
    .attr('stroke', 'black')
    .attr('stroke-dasharray', '4')
    .classed('ladder-side', true)
    .classed('ladder-end', true);

  return stageLadder.node();
};

export function LadderSVG(props: StageLadderNode): ReactElement {
  const {
    width,
    margin: { left: marginLeft = 0, right: marginRight = 0 } = {},
    scales,
    ladderData
  } = props;
  const height =
    scales.y(ladderData[0].lowerBound) -
    scales.y(ladderData[ladderData.length - 1].upperBound) +
    24;

  const svgRef = useRef<HTMLDivElement>();
  // Plot chart after JSX rendered
  useEffect(() => {
    const container = svgRef.current;
    if (!container) {
      return undefined;
    }

    d3.select(container)
      .append('svg:svg')
      .attr('class', 'ladder-svg')
      .attr('viewBox', `0 -24 ${width + marginLeft + marginRight} ${height}`)
      .attr('width', `${width + marginLeft + marginRight}px`)
      .attr('height', `${height}px`)
      .append(() => LadderNode({ ...props }));

    return () => {
      d3.select(container).selectAll('*').remove();
    };
  }, [props, width, height, marginLeft, marginRight]);

  return <div className="ladder-svg-holder" ref={svgRef} />;
}
